import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DataTable,
  type DataTableColumn,
  HairlineCard,
  Icons,
  iconSize,
  MenuItem,
  Stack,
  StatusChip,
  TextField,
  Typography,
  useSnackbar,
} from '@munifocus/ui';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  ComplaintImportResult,
  ComplaintImportRow,
  ComplaintImportRowError,
} from '@munifocus/contracts-gov';
import { AppShell } from '../components/AppShell';
import { PageIntro } from '../components/PageIntro';
import { useLocalGovernments } from '../lib/local-governments-api';
import { apiErrorMessage } from '../lib/mutation-toast';
import {
  COMPLAINT_IMPORT_REQUIRED_COLUMNS,
  importTemplateCsv,
  useImportComplaints,
} from '../lib/complaint-import-api';

const ACCEPT = '.csv,text/csv';

/**
 * Bulk intake — many files from one spreadsheet.
 *
 * Deliberately two-step, matching the Admin Portal's bulletin import: choosing
 * a file runs a **dry run** on the server and shows exactly what would happen —
 * which rows become files, which are duplicates of open files, which rows are
 * wrong and why — and only a second, explicit click writes anything.
 *
 * Bulk intake opens statutory files in someone's name, so showing the plan
 * before executing it is not politeness, it is the point. The preview and the
 * commit are the same server code path, so what is shown is what happens.
 */
export function BulkImport() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const registry = useLocalGovernments();
  const runImport = useImportComplaints();

  const [file, setFile] = useState<File | null>(null);
  const [localGovernmentId, setLocalGovernmentId] = useState('');
  const [preview, setPreview] = useState<ComplaintImportResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const governments = registry.data ?? [];
  // Pre-select on the same reasoning as intake: an officer appointed at one
  // municipality has only one right answer, and leaving it blank just adds a
  // click before the first useful one.
  const effectiveLg = localGovernmentId || (governments.length === 1 ? governments[0].id : '');

  const analyze = (chosen: File, lg: string) => {
    setPreview(null);
    runImport.mutate(
      { file: chosen, localGovernmentId: lg, dryRun: true },
      {
        onSuccess: setPreview,
        onError: (error) => snackbar.error(apiErrorMessage(error)),
      },
    );
  };

  const choose = (chosen: File | undefined) => {
    if (!chosen) return;
    setFile(chosen);
    if (effectiveLg) analyze(chosen, effectiveLg);
  };

  const commit = () => {
    if (!file || !effectiveLg) return;
    runImport.mutate(
      { file, localGovernmentId: effectiveLg, dryRun: false },
      {
        onSuccess: (result) => {
          setPreview(result);
          // Nothing landing is not a success, whatever the status code says —
          // every row was a duplicate or refused, and the table says which.
          if (result.created === 0) snackbar.warning(t('gov.bulkImport.noneCreated'));
          else snackbar.success(t('gov.bulkImport.created', { count: result.created }));
          // Straight to the dashboard only when the whole batch landed. With
          // anything skipped or rejected the officer needs to stay and read the
          // table — navigating away would hide the rows that need a decision.
          if (result.rowErrors.length === 0 && result.skipped === 0) navigate('/');
        },
        onError: (error) => snackbar.error(apiErrorMessage(error)),
      },
    );
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const downloadTemplate = () => {
    const blob = new Blob([importTemplateCsv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'munifocus-bulk-intake-template.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const committed = preview !== null && !preview.dryRun;
  const toCreate = preview?.rows.filter((r) => r.action === 'create').length ?? 0;

  const rowColumns = useMemo<DataTableColumn<ComplaintImportRow>[]>(
    () => [
      {
        key: 'rowNumber',
        label: t('gov.bulkImport.col.row'),
        cell: (r: ComplaintImportRow) => r.rowNumber,
        sortValue: (r: ComplaintImportRow) => r.rowNumber,
        nowrap: true,
      },
      {
        key: 'address',
        label: t('gov.file.address'),
        cell: (r: ComplaintImportRow) => r.address,
        sortValue: (r: ComplaintImportRow) => r.address,
        primary: true,
      },
      {
        key: 'reference',
        label: t('gov.file.number'),
        cell: (r: ComplaintImportRow) => r.reference ?? '—',
        sortValue: (r: ComplaintImportRow) => r.reference ?? '',
        mono: true,
        nowrap: true,
      },
      {
        key: 'action',
        label: t('gov.bulkImport.col.outcome'),
        cell: (r: ComplaintImportRow) => (
          <StatusChip
            label={t(
              // Tense follows the write: "Will create" is a promise before the
              // commit and a falsehood after it.
              r.action === 'create' && r.reference
                ? 'gov.bulkImport.action.created'
                : `gov.bulkImport.action.${r.action}`,
            )}
            status={r.action === 'create' ? 'success' : 'neutral'}
          />
        ),
        sortValue: (r: ComplaintImportRow) => r.action,
        nowrap: true,
      },
      {
        key: 'message',
        label: t('gov.bulkImport.col.note'),
        cell: (r: ComplaintImportRow) => r.message ?? '',
      },
    ],
    [t],
  );

  const errorColumns = useMemo<DataTableColumn<ComplaintImportRowError>[]>(
    () => [
      {
        key: 'rowNumber',
        label: t('gov.bulkImport.col.row'),
        cell: (e: ComplaintImportRowError) => e.rowNumber,
        sortValue: (e: ComplaintImportRowError) => e.rowNumber,
        nowrap: true,
      },
      {
        key: 'column',
        label: t('gov.bulkImport.col.column'),
        cell: (e: ComplaintImportRowError) => e.column ?? '—',
        sortValue: (e: ComplaintImportRowError) => e.column ?? '',
        mono: true,
        nowrap: true,
      },
      {
        key: 'message',
        label: t('gov.bulkImport.col.problem'),
        cell: (e: ComplaintImportRowError) => e.message,
        primary: true,
      },
    ],
    [t],
  );

  return (
    <AppShell>
      <PageIntro>{t('gov.bulkImport.intro')}</PageIntro>
      <Stack spacing={2}>
        <HairlineCard sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={2.5}>
            <TextField
              select
              size="small"
              label={t('gov.file.localGovernment')}
              helperText={t('gov.bulkImport.localGovernmentHelp')}
              value={effectiveLg}
              disabled={committed}
              onChange={(event) => {
                setLocalGovernmentId(event.target.value);
                // The anchor is part of what the preview reports — duplicates
                // are detected within one municipality — so re-run it.
                if (file) analyze(file, event.target.value);
              }}
              sx={{ maxWidth: 360 }}
            >
              {governments.map((government) => (
                <MenuItem key={government.id} value={government.id}>
                  {government.name}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Button size="small" variant="text" onClick={downloadTemplate}>
                {t('gov.bulkImport.downloadTemplate')}
              </Button>
              <Typography variant="caption">
                {t('gov.bulkImport.requiredColumns', {
                  columns: COMPLAINT_IMPORT_REQUIRED_COLUMNS.join(', '),
                })}
              </Typography>
            </Stack>

            {!committed && (
              <Box
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragging(false);
                  choose(event.dataTransfer.files[0]);
                }}
                onClick={() => inputRef.current?.click()}
                // The drop zone is also the file picker, so it answers the
                // keyboard the way the button it stands in for would.
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    inputRef.current?.click();
                  }
                }}
                sx={(theme) => ({
                  border: '1px dashed',
                  borderColor: dragging ? theme.palette.primary.main : theme.palette.divider,
                  backgroundColor: dragging ? theme.palette.action.hover : 'transparent',
                  borderRadius: 1.5,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'border-color 200ms, background-color 200ms',
                })}
              >
                <Icons.Upload size={iconSize.xl} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('gov.bulkImport.dropzone')}
                </Typography>
                <Typography variant="caption">{t('gov.bulkImport.dropzoneHint')}</Typography>
                <input
                  ref={inputRef}
                  type="file"
                  hidden
                  accept={ACCEPT}
                  onChange={(event) => choose(event.target.files?.[0])}
                />
              </Box>
            )}

            {file && (
              <Stack
                useFlexGap
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Typography variant="caption">{file.name}</Typography>
                {!committed && (
                  <Button size="small" variant="text" onClick={reset}>
                    {t('common.actions.clear')}
                  </Button>
                )}
              </Stack>
            )}

            {!effectiveLg && file && (
              <Alert severity="info">{t('gov.bulkImport.chooseLocalGovernment')}</Alert>
            )}

            {runImport.isPending && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <CircularProgress size={iconSize.md} />
                <Typography variant="body2">
                  {t(preview === null ? 'gov.bulkImport.checking' : 'gov.bulkImport.importing')}
                </Typography>
              </Stack>
            )}

            {preview?.fileError && <Alert severity="error">{preview.fileError}</Alert>}

            {preview && !preview.fileError && preview.ignoredColumns.length > 0 && (
              <Alert severity="warning">
                {t('gov.bulkImport.ignoredColumns', {
                  columns: preview.ignoredColumns.join(', '),
                })}
              </Alert>
            )}
          </Stack>
        </HairlineCard>

        {preview && !preview.fileError && (
          <HairlineCard sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">
                  {t(committed ? 'gov.bulkImport.resultTitle' : 'gov.bulkImport.previewTitle')}
                </Typography>
                <Typography variant="body2">
                  {committed
                    ? t('gov.bulkImport.resultSummary', {
                        created: preview.created,
                        skipped: preview.skipped,
                        failed: preview.rowErrors.length,
                      })
                    : t('gov.bulkImport.previewSummary', {
                        create: toCreate,
                        skipped: preview.skipped,
                        failed: preview.rowErrors.length,
                        place: preview.localGovernmentName ?? '',
                      })}
                </Typography>
              </Box>

              {preview.rows.length > 0 && (
                <DataTable
                  rows={preview.rows}
                  columns={rowColumns}
                  getRowKey={(row) => String(row.rowNumber)}
                  initialSortKey="rowNumber"
                  emptyLabel={t('gov.bulkImport.noRows')}
                  label={t('gov.bulkImport.previewTitle')}
                />
              )}

              {preview.rowErrors.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2">
                    {t('gov.bulkImport.errorsTitle', { count: preview.rowErrors.length })}
                  </Typography>
                  <Typography variant="caption">{t('gov.bulkImport.errorsHint')}</Typography>
                  <DataTable
                    rows={preview.rowErrors}
                    columns={errorColumns}
                    getRowKey={(row) => `${row.rowNumber}-${row.column ?? 'row'}`}
                    initialSortKey="rowNumber"
                    emptyLabel={t('gov.bulkImport.noErrors')}
                    label={t('gov.bulkImport.errorsTitle', {
                      count: preview.rowErrors.length,
                    })}
                  />
                </Stack>
              )}

              {!committed && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    disabled={toCreate === 0 || runImport.isPending}
                    onClick={commit}
                  >
                    {t('gov.bulkImport.commit', { count: toCreate })}
                  </Button>
                  <Button variant="outlined" onClick={reset} disabled={runImport.isPending}>
                    {t('common.actions.cancel')}
                  </Button>
                </Stack>
              )}

              {committed && (
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" onClick={() => navigate('/')}>
                    {t('gov.bulkImport.backToFiles')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </HairlineCard>
        )}
      </Stack>
    </AppShell>
  );
}
